import { useState } from "react";
import tw from "tailwind-styled-components";
import { toast } from "react-toastify";
import Config from "../../Config";
import { useSelector } from "react-redux";

function AddProductForm({ onProductAdded, onCancel }) {
      const user = useSelector((state) => state.UserReducer.user);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    stock_quantity: "",
    brand: "",
    slug: "",
    sku: "",
  });
  const [imageFile, setImageFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("price", formData.price);
      submitData.append("category_id", formData.category_id);
      submitData.append("stock_quantity", formData.stock_quantity);
      submitData.append("brand", formData.brand);
      submitData.append("slug", formData.slug);
      submitData.append("sku", formData.sku);
      
      if (imageFile) {
        submitData.append("images", imageFile);
      }

      const response = await fetch(`${Config.apiUrl}/products`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${user.token}`,
          // Don't set Content-Type for FormData, browser will set it automatically with boundary
        },
        body: submitData,
      });

      if (!response.ok) {
        throw new Error("Failed to create product");
      }

      const data = await response.json();
      // console.log("Product created:", data);
      
      // Reset form
      setFormData({
        name: "",
        description: "",
        price: "",
        category_id: "",
        stock_quantity: "",
        brand: "",
        slug: "",
        sku: "",
      });
      setImageFile(null);
      
      onProductAdded();
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("Failed to create product");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <FormTitle>Add New Product</FormTitle>
      
      <Form onSubmit={handleSubmit}>
        <FormRow>
          <FormGroup>
            <Label>Product Name *</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Nike Air Max"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Brand</Label>
            <Input
              type="text"
              name="brand"
              value={formData.brand}
              onChange={handleInputChange}
              placeholder="e.g., Nike"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Product description"
            rows="3"
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>Price *</Label>
            <Input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              placeholder="900"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Category ID *</Label>
            <Input
              type="number"
              name="category_id"
              value={formData.category_id}
              onChange={handleInputChange}
              placeholder="1"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Stock Quantity</Label>
            <Input
              type="number"
              name="stock_quantity"
              value={formData.stock_quantity}
              onChange={handleInputChange}
              placeholder="10"
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label>Slug</Label>
            <Input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              placeholder="product-slug"
            />
          </FormGroup>

          <FormGroup>
            <Label>SKU</Label>
            <Input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleInputChange}
              placeholder="SKU-123"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Product Image</Label>
          <FileInputWrapper>
            <FileInput
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imageFile && (
              <FileName>{imageFile.name}</FileName>
            )}
          </FileInputWrapper>
        </FormGroup>

        <ButtonGroup>
          <CancelButton type="button" onClick={onCancel}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Product"}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </FormContainer>
  );
}

export default AddProductForm;

const FormContainer = tw.div`
  bg-white p-4 sm:p-5 md:p-6 rounded-xl shadow-md mb-4 sm:mb-6 
  border border-gray-200
`;

const FormTitle = tw.h3`
  text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6
`;

const Form = tw.form`
  space-y-3 sm:space-y-4
`;

const FormRow = tw.div`
  grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4
`;

const FormGroup = tw.div`
  flex flex-col
`;

const Label = tw.label`
  text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2
`;

const Input = tw.input`
  px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg text-sm sm:text-base
  focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
  transition-all duration-200
`;

const TextArea = tw.textarea`
  px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg text-sm sm:text-base
  focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
  transition-all duration-200 resize-none
`;

const FileInputWrapper = tw.div`
  relative
`;

const FileInput = tw.input`
  px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg w-full text-sm
  file:mr-2 sm:file:mr-4 file:py-1.5 sm:file:py-2 file:px-3 sm:file:px-4 
  file:rounded-lg file:border-0 file:text-xs sm:file:text-sm 
  file:font-semibold file:bg-kuality-bg file:text-kuality-blue
  hover:file:bg-kuality-light hover:file:text-white cursor-pointer
`;

const FileName = tw.p`
  text-xs sm:text-sm text-gray-600 mt-1.5 sm:mt-2 truncate
`;

const ButtonGroup = tw.div`
  flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end mt-4 sm:mt-6
`;

const CancelButton = tw.button`
  w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-200 text-gray-700 
  rounded-lg font-semibold text-sm sm:text-base
  hover:bg-gray-300 active:bg-gray-400 transition-colors duration-200
`;

const SubmitButton = tw.button`
  w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-kuality-blue text-white 
  rounded-lg font-semibold text-sm sm:text-base
  hover:bg-kuality-dark active:bg-kuality-dark transition-colors duration-200
  disabled:opacity-50 disabled:cursor-not-allowed
  shadow-md hover:shadow-lg
`;